import { Button } from "@package/subframe/components/Button";
import { Loader } from "@package/subframe/components/Loader";
import { FeatherArrowLeft, FeatherPlus } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import EmergencyLightingView from "@/components/logs/EmergencyLightingView";
import FireAlarmTestView from "@/components/logs/FireAlarmTestView";
import FireDoorInspectionView from "@/components/logs/FireDoorInspectionView";
import FridgeTempPanel from "@/components/logs/FridgeTempPanel";
import GardenSupervisionView from "@/components/logs/GardenSupervisionView";
import HotHoldingPanel from "@/components/logs/HotHoldingPanel";
import LogEntryCard from "@/components/logs/LogEntryCard";
import LogEntryForm, { type FieldSchema } from "@/components/logs/LogEntryForm";
import SleepCheckView from "@/components/logs/SleepCheckView";
import { useNursery } from "@/contexts/NurseryContext";
import {
  getGetSessionEntriesQueryControllerGetQueryKey,
  getListTodaySessionsQueryControllerListQueryKey,
  useCreateLogEntryCommandControllerCreate,
  useGetSessionEntriesQueryControllerGet,
  useListTodaySessionsQueryControllerList,
} from "@/generated/log-types/log-types";
import { useOfflineHotHoldingBoard } from "@/hooks/use-offline-hot-holding-board";
import { useOfflineLogEntries } from "@/hooks/use-offline-log-entries";
import {
  FIRE_ALARM_TEST_LOG_TYPE_SLUG,
  FIRE_DOOR_INSPECTION_LOG_TYPE_SLUG,
} from "@/lib/fire-alarm-tests";
import {
  EMERGENCY_LIGHTING_LOG_TYPE_SLUG,
  FRIDGE_TEMPERATURE_LOG_TYPE_SLUG,
  GARDEN_SUPERVISION_LOG_TYPE_SLUG,
  SLEEP_CHECK_LOG_TYPE_SLUG,
} from "@/lib/monitoring";

export default function LogSessionPage() {
  const { logTypeId } = useParams({ from: "/_authenticated/logs/$logTypeId" });
  const search = useSearch({ from: "/_authenticated/logs/$logTypeId" }) as {
    coldStorageId?: string;
  };
  const { selectedNurseryId } = useNursery();
  const nurseryId = selectedNurseryId ?? "";
  const [showForm, setShowForm] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // The day view is reachable from the Workspace logs tab and the Monitoring
  // history tab (NAU-318), so back returns to wherever the user came from;
  // the Workspace logs tab is the fallback for direct loads.
  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate({ to: "/checklists", search: { tab: "logs" } });
    }
  };

  const { data: sessionsResponse } =
    useListTodaySessionsQueryControllerList(nurseryId);
  const sessions = sessionsResponse?.data;
  const currentSession = sessions?.find((s) => s.logTypeId === logTypeId);

  const { data: entriesResponse, isLoading: entriesLoading } =
    useGetSessionEntriesQueryControllerGet(currentSession?.sessionId ?? "", {
      query: { enabled: !!currentSession?.sessionId },
    });
  const entries = useMemo(() => entriesResponse?.data ?? [], [entriesResponse]);

  const createEntry = useCreateLogEntryCommandControllerCreate({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getListTodaySessionsQueryControllerListQueryKey(nurseryId),
        });
        if (currentSession?.sessionId) {
          queryClient.invalidateQueries({
            queryKey: getGetSessionEntriesQueryControllerGetQueryKey(
              currentSession.sessionId,
            ),
          });
        }
      },
    },
  });

  const handleSubmit = async (
    data: Record<string, unknown>,
    roomId?: string,
    notes?: string,
  ) => {
    await createEntry.mutateAsync({
      nurseryId,
      data: {
        logTypeId,
        data,
        roomId,
        notes,
      },
    });
    setShowForm(false);
  };

  const isSleepCheck =
    currentSession?.logTypeSlug === SLEEP_CHECK_LOG_TYPE_SLUG;
  const isFridgeTemp =
    currentSession?.logTypeSlug === FRIDGE_TEMPERATURE_LOG_TYPE_SLUG;
  const isGardenSupervision =
    currentSession?.logTypeSlug === GARDEN_SUPERVISION_LOG_TYPE_SLUG;
  const isHotHolding =
    currentSession?.logTypeSlug === "hot-holding-temperature";
  const isEmergencyLighting =
    currentSession?.logTypeSlug === EMERGENCY_LIGHTING_LOG_TYPE_SLUG;
  const isFireAlarmTest =
    currentSession?.logTypeSlug === FIRE_ALARM_TEST_LOG_TYPE_SLUG;
  const isFireDoorInspection =
    currentSession?.logTypeSlug === FIRE_DOOR_INSPECTION_LOG_TYPE_SLUG;

  // NAU-219d: fridge-temp submissions go through the offline queue so a
  // practitioner can log a reading with no connection — it lands in
  // IndexedDB immediately and syncs on reconnect. The server-side
  // incident-open + manager notification handlers run at sync time
  // (AC#13 + AC#15).
  // NAU-96: garden checks share the same offline path. The hook is owned here
  // (not inside the child view) so the header count and the view's list read
  // from one source of truth — otherwise the header shows a stale entryCount
  // from the today-sessions query, which the offline hook does not invalidate.
  // Every bespoke view that submits through `offlineLogEntries.createEntry`
  // must be listed here: the hook no-ops on a null nursery id, so an omission
  // makes the form silently record nothing while still reporting success.
  const offlineLogEntries = useOfflineLogEntries(
    isFridgeTemp ||
      isGardenSupervision ||
      isSleepCheck ||
      isEmergencyLighting ||
      isFireAlarmTest ||
      isFireDoorInspection
      ? nurseryId
      : null,
  );

  // NAU-93: hot holding owns its board hook here (not inside the panel) for the
  // same reason as above — the header's meal count reads from the live board,
  // not the stale today-sessions entryCount, and the panel shares that source.
  const hotHoldingBoard = useOfflineHotHoldingBoard(
    isHotHolding ? nurseryId : null,
  );
  const hotHoldingMealCount =
    hotHoldingBoard.awaitingService.length + hotHoldingBoard.completed.length;

  const gardenChecks = useMemo(
    () =>
      isGardenSupervision
        ? offlineLogEntries.entries.filter((e) => e.logTypeId === logTypeId)
        : [],
    [isGardenSupervision, offlineLogEntries.entries, logTypeId],
  );

  // Live count of this log type's *checks* from the shared offline hook — used
  // for the header so sleep/garden counts update instantly (incl. offline),
  // rather than reading the stale today-sessions entryCount. Sleep's
  // `session_end` markers are entries but not checks, so they're excluded.
  const liveCheckCount = useMemo(
    () =>
      offlineLogEntries.entries.filter(
        (e) =>
          e.logTypeId === logTypeId &&
          (e.data as Record<string, unknown>)?.type !== "session_end",
      ).length,
    [offlineLogEntries.entries, logTypeId],
  );

  const handleGardenCheck = async () => {
    await offlineLogEntries.createEntry({ logTypeId, data: {} });
  };

  const handleFridgeTempSubmit = async ({
    coldStorageId,
    coldStorageName,
    temperature,
    notes,
  }: {
    coldStorageId: string;
    coldStorageName: string;
    temperature: number;
    notes?: string;
  }) => {
    await offlineLogEntries.createEntry({
      logTypeId,
      data: { temperature },
      coldStorageId,
      coldStorageName,
      notes,
    });
  };

  // Read fridge entries from the shared offline hook (same source of truth as
  // sleep/garden), filtered to this log type. The hook already merges local
  // pending rows with server rows, dedupes synced locals, and maps
  // `coldStorage` onto both — so FridgeTempPanel's unit filter matches.
  //
  // Earlier this merged the orval `useGetSessionEntries` query (for synced
  // rows) with the offline hook's pending rows. But fridge submissions go
  // through the offline path, which never invalidates the orval queries — so
  // once a reading synced, the pending row was cleaned up while the orval list
  // stayed stale, and the entry disappeared from the list (NAU fridge-log-fixes).
  const fridgeEntries = useMemo(
    () =>
      isFridgeTemp
        ? offlineLogEntries.entries.filter((e) => e.logTypeId === logTypeId)
        : entries,
    [isFridgeTemp, offlineLogEntries.entries, logTypeId, entries],
  );

  return (
    <div className="flex h-full w-full flex-col items-start gap-6 overflow-y-auto bg-white">
      {/* Header */}
      <div className="flex w-full items-center gap-3">
        <button
          type="button"
          aria-label="Back"
          onClick={handleBack}
          className="flex items-center"
        >
          <FeatherArrowLeft className="h-5 w-5 text-subtext-color hover:text-default-font" />
        </button>
        <div className="flex grow flex-col items-start gap-0.5">
          <span className="text-metrics font-metrics text-primary-navy-800">
            {currentSession?.logTypeName ?? "Log"}
          </span>
          <span className="text-caption font-caption text-subtext-color">
            Today &middot;{" "}
            {isSleepCheck ||
            isGardenSupervision ||
            isEmergencyLighting ||
            isFireDoorInspection
              ? liveCheckCount
              : isFridgeTemp
                ? fridgeEntries.length
                : isHotHolding
                  ? hotHoldingMealCount
                  : (currentSession?.entryCount ?? 0)}{" "}
            {isSleepCheck ||
            isGardenSupervision ||
            isEmergencyLighting ||
            isFireDoorInspection
              ? "checks"
              : isHotHolding
                ? hotHoldingMealCount === 1
                  ? "meal"
                  : "meals"
                : "entries"}
          </span>
        </div>
        {!isSleepCheck &&
          !isFridgeTemp &&
          !isGardenSupervision &&
          !isHotHolding &&
          !isEmergencyLighting &&
          !isFireAlarmTest &&
          !isFireDoorInspection && (
            <Button
              icon={showForm ? undefined : <FeatherPlus />}
              onClick={() => setShowForm(!showForm)}
              variant={showForm ? "neutral-secondary" : "brand-primary"}
              size="medium"
            >
              {showForm ? "Cancel" : "Add Entry"}
            </Button>
          )}
      </div>

      {/* Sleep Check specialized view */}
      {isSleepCheck ? (
        <SleepCheckView
          logTypeId={logTypeId}
          entries={offlineLogEntries.entries}
          isLoading={offlineLogEntries.isLoading}
          isOffline={offlineLogEntries.isOffline}
          createEntry={offlineLogEntries.createEntry}
        />
      ) : isGardenSupervision ? (
        <GardenSupervisionView
          checks={gardenChecks}
          isLoading={offlineLogEntries.isLoading}
          isOffline={offlineLogEntries.isOffline}
          onRecordCheck={handleGardenCheck}
        />
      ) : isFridgeTemp ? (
        <FridgeTempPanel
          nurseryId={nurseryId}
          entries={fridgeEntries}
          entriesLoading={entriesLoading}
          initialColdStorageId={search.coldStorageId}
          onSubmit={handleFridgeTempSubmit}
          isSubmitting={false}
        />
      ) : isHotHolding ? (
        <HotHoldingPanel board={hotHoldingBoard} />
      ) : isFireAlarmTest ? (
        <FireAlarmTestView
          logTypeId={logTypeId}
          entries={offlineLogEntries.entries.filter(
            (e) => e.logTypeId === logTypeId,
          )}
          isLoading={offlineLogEntries.isLoading}
          isOffline={offlineLogEntries.isOffline}
          createEntry={offlineLogEntries.createEntry}
        />
      ) : isFireDoorInspection ? (
        <FireDoorInspectionView
          logTypeId={logTypeId}
          logTypeName={currentSession?.logTypeName ?? "Fire Door Inspections"}
          entries={offlineLogEntries.entries.filter(
            (e) => e.logTypeId === logTypeId,
          )}
          isLoading={offlineLogEntries.isLoading}
          isOffline={offlineLogEntries.isOffline}
          createEntry={offlineLogEntries.createEntry}
        />
      ) : isEmergencyLighting ? (
        <EmergencyLightingView
          logTypeId={logTypeId}
          entries={offlineLogEntries.entries.filter(
            (e) => e.logTypeId === logTypeId,
          )}
          isLoading={offlineLogEntries.isLoading}
          isOffline={offlineLogEntries.isOffline}
          createEntry={offlineLogEntries.createEntry}
        />
      ) : (
        <>
          {/* Entry Form */}
          {showForm && currentSession && (
            <div className="w-full rounded-lg border border-neutral-200 p-4">
              <LogEntryForm
                fieldSchema={
                  currentSession.fieldSchema as unknown as FieldSchema
                }
                roomApplicable={currentSession.roomApplicable}
                onSubmit={handleSubmit}
                isSubmitting={createEntry.isPending}
              />
            </div>
          )}

          {/* Entries List */}
          {entriesLoading ? (
            <div className="flex w-full items-center justify-center py-12">
              <Loader size="large" />
            </div>
          ) : entries.length === 0 ? (
            <div className="flex w-full flex-col items-center justify-center gap-2 py-12">
              <span className="text-body font-body text-subtext-color">
                No entries recorded today
              </span>
              {!showForm && (
                <Button
                  icon={<FeatherPlus />}
                  onClick={() => setShowForm(true)}
                  variant="brand-secondary"
                  size="medium"
                >
                  Add first entry
                </Button>
              )}
            </div>
          ) : (
            <div className="flex w-full flex-col items-start gap-3">
              {entries.map((entry) => (
                <LogEntryCard key={entry.id} entry={entry} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
